import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import { getPortalOrgInfo } from '../../services/portalApi';

const PortalLoginPage: React.FC = () => {
  const { orgAlias } = useParams<{ orgAlias: string }>();
  const { login } = usePortalAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [orgInfo, setOrgInfo] = useState<{ org_name: string; org_logo_url: string | null } | null>(null);

  useEffect(() => {
    if (orgAlias) {
      getPortalOrgInfo(orgAlias).then(setOrgInfo).catch(() => {});
    }
  }, [orgAlias]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { must_change_password } = await login(email, password, orgAlias!);
      if (must_change_password) {
        navigate(`/portal/${orgAlias}/change-password`);
      } else {
        navigate(`/portal/${orgAlias}/orders`);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('portal.login.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo / header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-600 mb-4 overflow-hidden">
            {orgInfo?.org_logo_url ? (
              <img src={orgInfo.org_logo_url} alt="logo" className="w-full h-full object-contain" />
            ) : (
              <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            )}
          </div>
          <h1 className="text-2xl font-bold text-white">{t('portal.login.title')}</h1>
          <p className="text-slate-400 text-sm mt-1">
            {orgInfo?.org_name ? <span className="text-blue-400 font-medium">{orgInfo.org_name}</span> : orgAlias ? <span className="text-blue-400 font-medium">{orgAlias}</span> : t('portal.login.signInTo')}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('portal.login.emailLabel')}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder={t('portal.login.emailPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('portal.login.passwordLabel')}</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-60 text-sm"
            >
              {loading ? t('portal.login.signingIn') : t('portal.login.signIn')}
            </button>
          </form>
        </div>

        <p className="text-center text-slate-500 text-xs mt-6">
          {t('portal.login.needHelp')}
        </p>
      </div>
    </div>
  );
};

export default PortalLoginPage;
